// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { ProjectAssetAssignment } from '@/project/api/types';
import { ProjectReportDatum } from '@/report/types';
import { formatNumber } from '@/utilities/number';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { PieArcDatum } from '@visx/shape/lib/shapes/Pie';
import { Accessor } from '@visx/shape/lib/types';
import { Text } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { MouseEvent, useState } from 'react';

const MARGIN = 8;
const DEFAULT_EMPTY_STATE = 'No projects';
const CENTER_LINE_HEIGHT_EM = 1.2;

interface ArcData {
  id: string;
  label: string;
  requestedCount: number;
  totalContractValue: number;
  totalContractValueByCurrency: Currency[];
  assignments: ProjectAssetAssignment[];
}

interface Props {
  /**
   * The data to display in the chart.
   */
  data: ProjectReportDatum[];

  /**
   * The width of the chart.
   */
  width: number;

  /**
   * The height of the chart.
   */
  height: number;

  /**
   * The content to display in the center of the chart, one line per entry.
   */
  children: string | number | string[];

  /**
   * If true, the chart is considered empty and the empty state is displayed. By
   * default, the chart is considered empty if there is no data.
   */
  isEmpty?: boolean;

  /**
   * The message to display when there is no data.
   *
   * @default 'No projects'
   */
  emptyState?: string;

  /**
   * A function that returns the color for a given project.
   */
  getColor: (id: string, index: number) => string;

  /**
   * A function that returns the value for a given project.
   */
  getValue: (datum: ProjectReportDatum) => number;

  /**
   * A function that is called when a project is hovered over.
   */
  onHover?: (id: string | null) => void;
}

/**
 * A pie chart that displays project data.
 */
export function ProjectChart(props: Props) {
  const theme = useTheme();
  const [hoverId, setHoverId] = useState<string | null>(null);

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const {
    hideTooltip,
    showTooltip,
    tooltipData,
    tooltipOpen,
    tooltipTop = 0,
    tooltipLeft = 0,
  } = useTooltip<ArcData>();

  const innerWidth = props.width - 2 * MARGIN;
  const innerHeight = props.height - 2 * MARGIN;
  const outerRadius = Math.min(innerWidth, innerHeight) / 2;
  const innerRadius = outerRadius * 0.6;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const top = MARGIN + centerY;
  const left = MARGIN + centerX;
  const isEmpty = props.isEmpty ?? props.data.length === 0;
  const centerLines = Array.isArray(props.children)
    ? props.children
    : [props.children];

  const getOuterRadius: Accessor<PieArcDatum<ProjectReportDatum>, number> = (
    datum
  ) => {
    if (hoverId === datum.data.id) {
      return outerRadius + 8;
    }

    return outerRadius;
  };

  const getPoint = (event: MouseEvent<SVGElement>) => {
    if (event.target instanceof SVGElement) {
      if (event.target.ownerSVGElement === null) {
        return null;
      }

      return localPoint(event.target.ownerSVGElement, event);
    }

    return null;
  };

  const createMouseMoveHandler =
    (data: ArcData) => (event: MouseEvent<SVGElement>) => {
      const point = getPoint(event);

      if (point === null) {
        return;
      }

      showTooltip({
        tooltipLeft: point.x,
        tooltipTop: point.y,
        tooltipData: data,
      });
    };

  if (isEmpty) {
    const emptyState = props.emptyState ?? DEFAULT_EMPTY_STATE;

    return (
      <Box
        height={props.height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">{emptyState}</Typography>
      </Box>
    );
  }

  return (
    <Box position="relative">
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[6],
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="overline" color="text.secondary" marginX={1}>
            {tooltipData.label}
          </Typography>
          <Divider />
          <Stack margin={1}>
            <Stack useFlexGap spacing={4} direction="row" alignItems="center">
              <Typography variant="subtitle2" color="text.primary">
                Total contract value
              </Typography>
              <Stack marginLeft="auto" alignItems="flex-end">
                {tooltipData.totalContractValueByCurrency.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrencyValue(null)}
                  </Typography>
                )}
                {tooltipData.totalContractValueByCurrency.map(
                  (contractValue) => (
                    <Typography
                      key={contractValue.isoCode}
                      variant="body2"
                      textAlign="right"
                      color="text.secondary"
                    >
                      {formatCurrencyValue(contractValue)}
                    </Typography>
                  )
                )}
              </Stack>
            </Stack>
            <Typography variant="subtitle2" color="text.primary" marginTop={1}>
              Planned asset count
            </Typography>
            {tooltipData.assignments.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No products configured
              </Typography>
            )}
            {tooltipData.assignments.map((assignment) => (
              <Stack
                key={assignment.id}
                spacing={2}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.primary">
                  {assignment.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="right"
                >
                  {formatNumber(assignment.requestedCount)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </TooltipInPortal>
      )}
      <svg ref={containerRef} width={props.width} height={props.height}>
        <Group top={top} left={left}>
          <Pie
            data={props.data}
            outerRadius={getOuterRadius}
            innerRadius={innerRadius}
            cornerRadius={3}
            padAngle={0.005}
            pieValue={props.getValue}
            pieSortValues={null}
            pieSort={null}
          >
            {(pie) => {
              return pie.arcs.map((arc, index) => {
                const path = pie.path(arc);
                const fill = props.getColor(arc.data.id, index);

                if (path === null) {
                  return null;
                }

                const handleMouseEnter = () => {
                  props.onHover?.(arc.data.id);
                  setHoverId(arc.data.id);
                };

                const handleMouseOut = () => {
                  hideTooltip();
                  setHoverId(null);
                  props.onHover?.(null);
                };

                return (
                  <path
                    key={arc.data.id}
                    d={path}
                    fill={fill}
                    onMouseEnter={handleMouseEnter}
                    onMouseOut={handleMouseOut}
                    onMouseMove={createMouseMoveHandler(arc.data)}
                  />
                );
              });
            }}
          </Pie>
          {centerLines.map((line, lineIndex) => (
            <Text
              key={lineIndex}
              textAnchor="middle"
              verticalAnchor="middle"
              dy={`${(lineIndex - (centerLines.length - 1) / 2) * CENTER_LINE_HEIGHT_EM}em`}
              fontSize={theme.typography.h4.fontSize}
              fontWeight={theme.typography.fontWeightBold}
            >
              {line}
            </Text>
          ))}
        </Group>
      </svg>
    </Box>
  );
}
